import moment from "moment";
import { APP_ELEMENT_DIMENSIONS, DEFAULT_SCORING_RATING } from "../constants";
import { ICohort, ICohortComment, IPeriod } from "../stores/jotai/base";
import { TeamCollections } from "../stores/jotai/teams/teams";

interface IPeriodRating {
    userID: string;
    ratings: number[];
    avgRating: number;
}

type IPeriodRatings = IPeriodRating[];

export const calculateStarColor = (value) => {
    if (value == 0) {
        return "#c7c7c7";
    }

    if (value == 1) {
        return "#CC2529";
    }

    if (value == 2) {
        return "#D35E60";
    }

    if (value == 3) {
        return "#DA7C30";
    }

    if (value == 4) {
        return "#84BA5B";
    }

    if (value == 5) {
        return "#3E9651";
    }
};

export const getScoringCommentsByPeriod: (params: {
    hasAccessToScoringPage: boolean;
    teamID: string;
    cohortComments: ICohortComment[];
    selectedPeriodIndex: number;
    excludeCommentIDs: string[];
}) => ICohortComment[] = ({
    hasAccessToScoringPage,
    teamID,
    cohortComments,
    selectedPeriodIndex,
    excludeCommentIDs = [],
}) => {
    if (!hasAccessToScoringPage || !teamID) {
        return null;
    }

    let comments = cohortComments?.filter((comment) => {
        if (!excludeCommentIDs.includes(comment.commentID)) {
            return selectedPeriodIndex !== undefined
                ? comment.iterator === selectedPeriodIndex &&
                        comment.groupID === teamID &&
                        comment.slug === "instructor"
                : comment.groupID === teamID &&
                        comment.slug === "instructor";
        }

        return false;
    });

    comments?.sort((comment1, comment2) => {
        const comment1Iterator = moment(comment1.iterator);
        const comment2Iterator = moment(comment2.iterator);

        if (comment1Iterator.isAfter(comment2Iterator)) {
            return 1;
        }

        if (comment1Iterator.isBefore(comment2Iterator)) {
            return -1;
        }

        return 0;
    });

    return comments;
};

export const getCurrentComment: (
    hasAccessToScoringPage: boolean,
    teamID: string,
    cohortComments: ICohortComment[],
    selectedPeriodIndex: number,
    userID: string
) => ICohortComment | null = (
    hasAccessToScoringPage,
    teamID,
    cohortComments,
    selectedPeriodIndex,
    userID
) => {
    if (!hasAccessToScoringPage || !teamID) {
        return null;
    }

    const comment = cohortComments?.find(
        (comment) =>
            comment.iterator === selectedPeriodIndex &&
            comment.groupID === teamID &&
            comment.userID === userID &&
            comment.slug === "instructor"
    );

    return comment || null;
};

export const buildCurrentRatings = (comment) => {
    if (!comment) {
        return [];
    }

    let ratings: string[] = comment.rating.split(",");

    if (ratings[ratings.length - 1] === ",") {
        ratings = ratings.slice(0, -1);
    }

    ratings = ratings.filter((rating) => rating !== "");

    return ratings.map((rating) => parseInt(rating, 10));
};

export const preparePeriodWisRatings: (
    teamID: string,
    periods: IPeriod[],
    comments: ICohortComment[]
) => IPeriodRatings[] = (teamID, periods, comments) => {
    const periodWiseRatings = periods.map((period) => []);

    comments?.forEach((comment) => {
        // Comment is for the correct week and is an "instructor comment"
        if (teamID == comment?.groupID && comment?.slug == "instructor") {
            const ratings = buildCurrentRatings(comment);
            const ratingSum = ratings.reduce(
                (accumulator, rating) => accumulator + rating,
                0
            );
            const ratingAvg = ratingSum / ratings.length;

            periodWiseRatings[comment?.iterator]?.push({
                userID: comment.userID,
                ratings,
                avgRating: ratingAvg,
            });
        }
    });

    return periodWiseRatings;
};

export const getPeriodWiseAvgRating: (
    periodRatings: IPeriodRatings[]
) => number[] = (periodRatings) =>
    periodRatings.map(
        (ratings) =>
            ratings.reduce(
                (sum, rating) => sum + rating.avgRating / ratings.length,
                0
            )
        // will return array like [0, 2.1, 0, 4, 1.2, ...]
    );

export const buildPresentationScoreChart = (
    cohort: ICohort,
    teamID: string,
    comments: ICohortComment[],
    teams: TeamCollections,
    periods: IPeriod[]
) => {
    const currentTeam = teams[teamID];
    const periodWiseRatings = preparePeriodWisRatings(teamID, periods, comments);
    const periodWiseAvgRating = getPeriodWiseAvgRating(periodWiseRatings);
    let currentTitle = currentTeam
        ? `${currentTeam.name} Average Presentation Scores`
        : "Loading Presentations";

    const template = {
        chart: {
            type: "column",
            height: APP_ELEMENT_DIMENSIONS.SCORING.STATS.HEIGHT,
        },
        title: {
            text: currentTitle,
            style: {
                fontSize: "15px",
            },
        },
        xAxis: {
            categories: periodWiseRatings.map(
                (_rating, index) => `${cohort.periodLabel} ${index + 1}`
            ),
            title: {
                text: null,
            },
        },
        yAxis: {
            min: 0,
            max: 5,
            categories: ["1", "2", "3", "4", "5"],
            title: {
                text: null,
            },
            labels: {
                enabled: true,
            },
            gridLineColor: "transparent",
            plotLines: [
                {
                    value: 0,
                    dashStyle: "shortdash",
                    color: calculateStarColor(DEFAULT_SCORING_RATING),
                    width: 1,
                },
            ],
        },
        legend: {
            enabled: false,
        },
        plotOptions: {
            bar: {
                dataLabels: {
                    enabled: false,
                },
            },
        },
        colors: ["#CF21FF", "#DC4A3E", "#CF21FF", "#FFC733", "#24C339", "#1B86E8"],
        credits: {
            enabled: false,
        },
        series: [
            {
                name: "Average Scores",
                data: periodWiseAvgRating.map((rating) => ({
                    y: Math.round(rating * 100) / 100 || null,
                    color: calculateStarColor(Math.round(rating)),
                })),
            },
        ],
    };

    return template;
};
